// 큰 탕 뒤 눈 지붕 쉼터, 오래된 목욕탕 건물(창호지 창·노렌), 문 앞 등불. 프로토타입 468–518.
import * as THREE from 'three';
import { mk } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { rnd } from '../../engine/math';
import { LEGACY } from '../../engine/lights';
import { FRONT } from './layout';
import { iceMat, logWood, oldWood, snowMat, stoneMat } from './materials';
import type { QuietScene } from './scene';

/** 눈이 두껍게 쌓인 맞배지붕과 처마 고드름 (쉼터·목욕탕·대기 벤치). */
export function snowRoof(group: THREE.Object3D, w: number, d: number, y: number, pitch: number, mat: THREE.Material): void {
  [-1, 1].forEach((sd) => {
    const run = d / 2, len = run / Math.cos(pitch);
    const slab = mk(new THREE.BoxGeometry(w, 0.16, len), mat); slab.position.set(0, y + run * Math.tan(pitch) / 2, sd * run / 2); slab.rotation.x = sd * pitch; group.add(slab);
    const sn = mk(new THREE.BoxGeometry(w + 0.25, 0.5, len + 0.2), snowMat); sn.position.set(0, y + run * Math.tan(pitch) / 2 + 0.32, sd * run / 2); sn.rotation.x = sd * pitch; group.add(sn);
    const lip = mk(new THREE.CylinderGeometry(0.26, 0.26, w + 0.25, 12), snowMat); lip.rotation.z = Math.PI / 2; lip.position.set(0, y + 0.05, sd * (run + 0.05)); group.add(lip);
    for (let x = -w / 2 + 0.2; x < w / 2; x += 0.35 + rnd(x * 9 + sd) * 0.4) {
      const ic = mk(new THREE.ConeGeometry(0.035, 0.22 + rnd(x * 13 + sd) * 0.35, 6), iceMat); ic.rotation.x = Math.PI; ic.position.set(x, y - 0.25, sd * (run + 0.05)); ic.castShadow = false; group.add(ic);
    }
  });
}

/** 등불 세기 배율 (r128 → 0.169, lights.ts LEGACY). */
export const LANTERN_K = LEGACY;

export function buildBuildings(Q: QuietScene): THREE.PointLight {
  const { world, L } = Q, BIG = L.big;
  // 큰 탕 뒤 통나무 기둥 쉼터
  {
    const g = new THREE.Group(), R = BIG.rAt(-FRONT); g.position.set(BIG.x, 0, BIG.z - R - 0.95); world.add(g);
    ([[-2.7, -0.4], [2.7, -0.4], [-2.6, 2.4], [2.6, 2.4]] as const).forEach(([x, z]) => {
      const post = mk(new THREE.CylinderGeometry(0.13, 0.16, 2.6, 8), logWood); post.position.set(x, 1.3 - (z > 1 ? 0.15 : 0), z); post.rotation.z = (rnd(x + z) * 0.06 - 0.03); g.add(post);
      if (z < 0) L.obstacles.push({ x: BIG.x + x, z: BIG.z - R - 0.95 + z, r: 0.3 });
    });
    [-0.4, 2.4].forEach((z) => { const beam = mk(new THREE.CylinderGeometry(0.11, 0.11, 6.0, 8), logWood); beam.rotation.z = Math.PI / 2; beam.position.set(0, 2.55, z); g.add(beam); });
    const roofG = new THREE.Group(); roofG.position.z = 1.0; g.add(roofG);
    snowRoof(roofG, 6.8, 4.4, 2.65, 0.42, plush('#6B5A44', '#958368', 0.03, true));
  }
  // 눈 지붕을 인 오래된 목욕탕. 가운데 문으로 료칸 식당에 들어간다.
  {
    const g = new THREE.Group(); g.position.set(0, 0, -13.2); world.add(g);
    const W = 8, D = 4.6, H = 3.0;
    const found = mk(new THREE.BoxGeometry(W + 0.3, 0.4, D + 0.3), stoneMat('#7D817C')); found.position.y = 0.2; g.add(found);
    const body = mk(new THREE.BoxGeometry(W, H, D), oldWood); body.position.y = 0.4 + H / 2; g.add(body);
    [-W / 2, -W / 4, 0, W / 4, W / 2].forEach((x) => { const p = mk(new THREE.BoxGeometry(0.18, H, 0.18), plush('#3E3024', '#5E4C3C', 0.02)); p.position.set(x, 0.4 + H / 2, D / 2 + 0.02); g.add(p); });
    const shoji = (() => {
      const c = document.createElement('canvas'); c.width = 256; c.height = 192; const x = c.getContext('2d')!;
      x.fillStyle = '#F6E2B8'; x.fillRect(0, 0, 256, 192); x.strokeStyle = '#4A3828'; x.lineWidth = 7;
      for (let i = 0; i <= 6; i++) { x.beginPath(); x.moveTo(i * 256 / 6, 0); x.lineTo(i * 256 / 6, 192); x.stroke(); }
      for (let j = 0; j <= 4; j++) { x.beginPath(); x.moveTo(0, j * 192 / 4); x.lineTo(256, j * 192 / 4); x.stroke(); }
      return new THREE.CanvasTexture(c);
    })();
    const winMat = new THREE.MeshStandardMaterial({ map: shoji, emissive: 0xFFD9A0, emissiveMap: shoji, emissiveIntensity: 0.55, roughness: 1 });
    [-2.6, 2.6].forEach((x) => { const wdw = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 1.4), winMat); wdw.position.set(x, 2.1, D / 2 + 0.03); g.add(wdw); });
    const door = mk(new THREE.BoxGeometry(1.5, 2.2, 0.1), plush('#2A211A', '#2A211A', 0)); door.position.set(0, 1.5, D / 2 + 0.02); g.add(door);
    const noren = (() => {
      const c = document.createElement('canvas'); c.width = 256; c.height = 192; const x = c.getContext('2d')!;
      x.fillStyle = '#2E3E66'; x.fillRect(0, 0, 256, 192); x.fillStyle = '#1E2A48'; x.fillRect(126, 40, 4, 152);
      x.fillStyle = '#F4F1EA'; x.font = '700 120px "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif CJK JP", serif'; x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText('ゆ', 128, 100);
      return new THREE.CanvasTexture(c);
    })();
    const nr = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1.2), new THREE.MeshStandardMaterial({ map: noren, roughness: 1, side: THREE.DoubleSide })); nr.position.set(0, 2.0, D / 2 + 0.12); g.add(nr);
    const roofG = new THREE.Group(); g.add(roofG);
    snowRoof(roofG, W + 1.2, D + 1.4, 0.4 + H, 0.48, plush('#3E3A36', '#66605A', 0.02, true));
    [-1, 1].forEach((sd) => {
      const tri = new THREE.Shape([new THREE.Vector2(-(D + 1.4) / 2, 0), new THREE.Vector2((D + 1.4) / 2, 0), new THREE.Vector2(0, (D + 1.4) / 2 * Math.tan(0.48))]);
      const gm = new THREE.Mesh(new THREE.ShapeGeometry(tri), plush('#4E3E30', '#4E3E30', 0)); (gm.material as THREE.Material).side = THREE.DoubleSide; gm.rotation.y = Math.PI / 2; gm.position.set(sd * W / 2, 0.4 + H, 0); g.add(gm);
    });
    L.obstacles.push({ rect: true, x: 0, z: -13.2, hw: W / 2 + 0.3, hd: D / 2 + 0.3 });
    const sign = new THREE.Group(); sign.position.set(-3.4, 0, -10.2); world.add(sign);
  }
  const lanternLight = new THREE.PointLight(0xFFC27A, 0.8 * LANTERN_K, 7, 2); lanternLight.position.set(0, 2.4, -10.2); Q.scene.add(lanternLight);
  return lanternLight;
}
